// ============================================================================
// FSA STATEMENT SCREEN  (FSA-2.2 Wave 3)
// ============================================================================
// Renders one Statement (Balance Sheet, Profit & Loss, Cash Flow) inside
// the FSA workspace. The screen walks the report's layout JSON and shows
// one row per line:
//
//   • SECTION_HEADER → full-width bold band, no value cells.
//   • NOTE_TOTAL     → label + CY / PY amounts (read-only) + source-Note
//                       status chip + per-row drift chip + action menu
//                       ("Open Note", "Refresh from Note").
//   • VALUE          → label + CY / PY amounts (read-only — auditor
//                       authors the underlying Notes, then refreshes).
//   • SUBTOTAL/TOTAL → label + formula-evaluated amounts (read-only).
//
// Statement values are STRICTLY read-only. The auditor authors the
// underlying Notes; the Statement refreshes from them. Editing on the
// Statement itself is disallowed (audit independence still applies, but
// at the Note layer — the Statement is the formal output).
//
// Header chrome:
//   • Status chip / unsaved-changes chip / Display-mode toggle
//     (Actual / Rounded) / BS reconciliation chip (Total E&L vs Total
//     Assets, only visible on the Balance Sheet).
//
// Action toolbar:
//   • Refresh all from Notes — pulls every line's value from the live
//     evaluation that the hydrate produced. Marks the workspace dirty
//     so Save activates.
//   • Save / Submit / Approve / Reopen — same state machine as Notes.
//   • Preview — placeholder for PDF preview hookup.
//
// Pagination: the layout is paginated client-side using SyutGrid's
// visual chrome (`.pagination-footer` + `.page-btn`). A Statement
// layout typically holds 40–80 rows, so paging keeps the visible
// surface manageable without server round-trips.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { ActionMenu, type ActionMenuItem } from 'components/ui';
import notificationService from 'services/notificationService';
import type { ID } from 'api/types';
import type {
  EvaluatedLine,
  StatementLayout,
  StatementLayoutLine,
  StatementWorkspaceData,
  SubsectionItemData,
} from 'api/types/fsa/statementData.types';
import { useFsaStatementHydrate } from 'hooks/api/fsa/useFsaStatements';
import { fsaStatementsService } from 'api/services/fsa/fsaStatementsService';
import FsaNoteModal from 'components/modals/modules/fsa/FsaNoteModal';
import FsaStatementPdfPreviewModal from 'components/modals/modules/fsa/FsaStatementPdfPreviewModal';
import FsaCfSubsectionWorkingsModal from 'components/modals/modules/fsa/FsaCfSubsectionWorkingsModal';
import { evaluatedToLine } from './statementLines';
// FSA-2.4 S1-C — DriftChip / LedgerDriftChip retired from Statement.
// Per-line drift surfaces as muted dots; aggregate counts live in
// the top Deviations panel.
import NoteDeviationPanel, { type NoteDeviationItem } from 'components/modals/modules/fsa/notes/panes/shared/NoteDeviationPanel';

interface Props {
  engagementId: ID;
  reportId: ID;
  reportCode: string;
  reportName: string;
  /** Minimize-to-bar context for the in-place Note Modal opened from this screen. */
  tabId?: string;
  moduleName?: string;
}

const EMPTY_WORKSPACE: StatementWorkspaceData = {
  lines: {},
  notes_text: null,
  _hidden_lines: [],
};

/**
 * FSA-2.4 — canonical JSON stringify used ONLY for dirty-tracking
 * comparisons. Sorts object keys recursively so the comparison is
 * insensitive to insertion order. Necessary because PostgreSQL JSONB
 * normalises key order on store (length, then alpha) while the FE's
 * evaluatedToLine builder produces keys in a different order. Without
 * canonicalisation, every save → refetch → re-overlay cycle would
 * produce a "different" string even when the underlying data is
 * identical, leaving the Save button stuck active after a successful
 * save. The actual save payload still uses plain JSON.stringify —
 * canonicalisation is purely a comparison-side concern.
 */
function canonicalStringify(value: unknown): string {
  return JSON.stringify(value, (_, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const sorted: Record<string, unknown> = {};
      for (const k of Object.keys(v).sort()) sorted[k] = (v as Record<string, unknown>)[k];
      return sorted;
    }
    return v;
  });
}

const FsaStatementScreen: React.FC<Props> = ({
  engagementId,
  reportId,
  reportCode,
  reportName,
  tabId,
  moduleName,
}) => {
  // FSA-2.2 Wave 3 — open the Note Modal in place when the auditor
  // clicks "Open <Note>" on a NOTE_TOTAL row's action menu. Modal
  // stacks ABOVE the Statement; closing returns the auditor right here
  // (no tab flip, no list detour).
  const [openNoteScheduleId, setOpenNoteScheduleId] = useState<ID | null>(null);
  const { data: hydrate, loading, error, refetch } = useFsaStatementHydrate(engagementId, reportId);

  // Workspace lives in component state — auditor edits flow here, Save
  // serializes back to the API. Snapshot-based dirty tracking.
  const [workspace, setWorkspace] = useState<StatementWorkspaceData>(EMPTY_WORKSPACE);
  const initialSnapshotRef = useRef<string>('');
  // Last-saved line values, kept alongside the snapshot string so individual
  // rows can be compared field-by-field. The stringified snapshot answers
  // "is anything dirty?"; this answers "is THIS row dirty?", which is what
  // the per-row unsaved highlight needs.
  const [savedLines, setSavedLines] = useState<StatementWorkspaceData['lines']>({});

  // Parsed layout — only re-parse when hydrate changes.
  const layout = useMemo<StatementLayout | null>(() => {
    if (!hydrate?.layoutJson) return null;
    try { return JSON.parse(hydrate.layoutJson) as StatementLayout; }
    catch { return null; }
  }, [hydrate?.layoutJson]);

  // Initialise workspace when hydrate lands.
  //
  // SAVED-AS-DISPLAYED. The Statement shows what is PERSISTED — the same
  // data the PDF renders from — so the tally state on screen is the tally
  // state in the printed deliverable. Always.
  //
  // FSA-2.4 previously overlaid the LIVE evaluation onto every non-override
  // line here, so the grid showed freshly-evaluated figures that existed
  // only in the browser. That created a genuine audit hazard: an auditor
  // could see a tallied Balance Sheet, print it, and issue an UNTALLIED one,
  // because the print engine renders saved data only. Screen and deliverable
  // must never disagree about whether the statement balances.
  //
  // The problem the overlay solved is still solved, just visibly instead of
  // silently: a P&L saved before Note 35 was populated now DISPLAYS the
  // stale figure, flags it (per-row drift dots, the "live drift" chip, and
  // the Deviations panel's "N lines differ from the latest Notes"), and one
  // click on Save refreshes from the Notes and persists in a single step.
  // A stale number you can see and fix beats a fresh number that isn't in
  // the file.
  //
  // Seeds ONCE per persisted version, NOT on every hydrate. refetch() is
  // called for reasons that have nothing to do with the saved data changing
  // — Refresh All and Refresh Line both re-fetch first, precisely so they
  // apply the freshest evaluation. Re-seeding on hydrate identity would
  // reset the workspace back to saved immediately after those handlers write
  // the refreshed values into it, so Refresh would silently do nothing.
  // (Invisible before saved-as-displayed, because the seed re-applied live
  // anyway and both paths produced the same figures.)
  //
  // rowVersion moves only when the statement is actually persisted, so it is
  // the correct trigger for "re-read the saved state". `unsaved` covers a
  // statement that has never been saved — it still seeds once.
  const savedIdentity = `${reportId ?? ''}::${hydrate?.workspace?.rowVersion ?? 'unsaved'}`;
  const seededForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hydrate) return;
    if (seededForRef.current === savedIdentity) return;
    seededForRef.current = savedIdentity;

    // Step 1 — build the SAVED state from the persisted workspace blob.
    // This is the dirty-tracking baseline: any subsequent change
    // (auditor edits OR auto-refresh from live below) makes
    // JSON.stringify(workspace) !== savedSnapshot so Save activates.
    let savedState: StatementWorkspaceData = { ...EMPTY_WORKSPACE, lines: {} };
    if (hydrate.workspace?.data) {
      try {
        const parsed = JSON.parse(hydrate.workspace.data) as StatementWorkspaceData;
        savedState = { ...EMPTY_WORKSPACE, ...parsed, lines: parsed.lines ?? {} };
      } catch {
        // fall through to seed-from-live
      }
    }
    const savedSnapshot = canonicalStringify(savedState);

    // Step 2 — display the saved state verbatim. No live overlay: the grid,
    // the totals and every tally check now run on exactly what is persisted.
    //
    // A statement that is out of step with its Notes is NOT silently
    // corrected here — it is reported. `hasLiveDrift` compares saved against
    // hydrate.liveEvaluation and lights the "live drift" chip, the per-row
    // dots and the Deviations entry; Save then refreshes and persists in one
    // click (see the Save handler's auto-apply). Overrides are untouched by
    // that refresh, as before.
    setWorkspace(savedState);

    // Baseline for dirty tracking AND for the per-row "edited, not saved"
    // highlight. Nothing has been edited at hydrate time, so Save correctly
    // starts inactive — drift alone enables it via hasLiveDrift.
    setSavedLines(savedState.lines);
    initialSnapshotRef.current = savedSnapshot;
  }, [hydrate, savedIdentity]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return canonicalStringify(workspace) !== initialSnapshotRef.current;
  }, [workspace]);

  // Refresh all — replace every line's value with the live evaluation.
  // Auditor overrides are RESET (override is per-line — if you want to
  // keep one, Refresh per-line on the others instead). Always re-fetches
  // hydrate first so liveEvaluation reflects the latest Note saves /
  // Imports (BOTH CY closing AND PY opening). Using the cached
  // hydrate.liveEvaluation here would silently apply stale values — the
  // screen was opened before the most recent Note write, so PY would
  // come through as 0 even though the Note's opening_actual_total is
  // already populated server-side.
  const handleRefreshAll = useCallback(async () => {
    const fresh = await refetch();
    const live = fresh?.liveEvaluation;
    if (!live) return;
    setWorkspace(prev => {
      const next: StatementWorkspaceData = { ...prev, lines: { ...prev.lines } };
      for (const [code, ev] of Object.entries(live)) {
        // FSA-2.4 S3-B — Preserve auditor overrides through Refresh All.
        // `is_override: true` = explicit auditor intent; auto-refresh
        // would silently clobber it. Auditor must explicitly clear an
        // override to opt back into auto-refresh for that line.
        if (prev.lines[code]?.is_override) continue;
        next.lines[code] = evaluatedToLine(ev);
      }
      return next;
    });
  }, [refetch]);

  // Per-line refresh from its source Note. Same re-fetch policy as
  // Refresh All — pull fresh evaluation for THIS line before applying,
  // so CY closing AND PY opening both reflect the latest Note save.
  const handleRefreshLine = useCallback(async (lineCode: string) => {
    const fresh = await refetch();
    const ev = fresh?.liveEvaluation?.[lineCode];
    if (!ev) return;
    setWorkspace(prev => ({
      ...prev,
      lines: { ...prev.lines, [lineCode]: evaluatedToLine(ev) },
    }));
  }, [refetch]);

  const handleNavigateToNote = useCallback((lineCode: string) => {
    const ev = hydrate?.liveEvaluation?.[lineCode];
    if (!ev?.sourceScheduleId) {
      notificationService.toast.info('This line is not linked to a Note.');
      return;
    }
    setOpenNoteScheduleId(ev.sourceScheduleId);
  }, [hydrate?.liveEvaluation]);

  // FSA-2.4 CF4 — Working Details modal state for CF subsections.
  // Each SUBSECTION layout line opens its own item-editor (modal-xl).
  // On save, the modal returns a new items list; we write it into
  // workspace.subsections[code] so the existing Save flow persists.
  const [openSubsection, setOpenSubsection] = useState<{ code: string; label: string } | null>(null);
  const [importPriorPyConfirmOpen, setImportPriorPyConfirmOpen] = useState(false);
  const [importPriorPyBusy, setImportPriorPyBusy] = useState(false);
  const handleImportPriorPy = useCallback(async () => {
    setImportPriorPyBusy(true);
    try {
      await fsaStatementsService.importPriorPy(engagementId, reportId);
      setImportPriorPyBusy(false);
      setImportPriorPyConfirmOpen(false);
      notificationService.toast.success('Prior-year values imported from prior CF.');
      await refetch();
    } catch (err) {
      setImportPriorPyBusy(false);
      const msg = err instanceof Error ? err.message : 'Import failed.';
      notificationService.toast.error(msg);
    }
  }, [engagementId, reportId, refetch]);
  const handleOpenSubsection = useCallback((code: string, label: string) => {
    setOpenSubsection({ code, label });
  }, []);
  const handleSaveSubsection = useCallback((items: SubsectionItemData[]) => {
    if (!openSubsection) return;
    setWorkspace((prev) => ({
      ...prev,
      subsections: {
        ...(prev.subsections ?? {}),
        [openSubsection.code]: { items },
      },
    }));
    setOpenSubsection(null);
    notificationService.toast.success(`${openSubsection.label || openSubsection.code} updated. Click Save to persist.`);
  }, [openSubsection]);

  // Statement values are read-only — the auditor authors the
  // underlying Notes, then refreshes the Statement (per-line or via
  // "Refresh all from Notes"). No inline editing on the Statement
  // itself, so no per-cell change handler.

  // Display mode — toggles whether amount columns show actual or
  // rounded figures. Mirrors the Note Modal's Actual/Rounded tabs.
  // Rounded values come straight from `amount_rounded` slots in the
  // saved workspace; the evaluator pre-fills them from the source Note.
  const [displayMode, setDisplayMode] = useState<'actual' | 'rounded'>('actual');

  // FSA-2.4.4 — Pagination retired. A statement is a bounded document
  // (40–80 lines), not a paginated grid: it renders in full and scrolls,
  // maximising the lines visible during review.

  // FSA-2.4.1 — Summary strip collapse state. Persisted in localStorage
  // so the auditor's preference survives modal close + tab switch. The
  // strip + Deviations panel + header chrome together used to eat most
  // of the vertical space on viewports below 900px; collapsing the
  // strip recovers ~80px of table room.
  const SUMMARY_COLLAPSED_KEY = 'fsa.statement.summary-collapsed';
  const [summaryStripCollapsed, setSummaryStripCollapsedState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try { return window.localStorage.getItem(SUMMARY_COLLAPSED_KEY) === 'true'; }
    catch { return false; }
  });
  const setSummaryStripCollapsed = useCallback((updater: (prev: boolean) => boolean) => {
    setSummaryStripCollapsedState(prev => {
      const next = updater(prev);
      try { window.localStorage.setItem(SUMMARY_COLLAPSED_KEY, String(next)); }
      catch { /* noop */ }
      return next;
    });
  }, []);

  // PDF preview — modal opens with a fresh server-rendered blob each
  // time. We pass the engagement + report context plus the current
  // displayMode so rounded / actual mirrors the screen toggle.
  const [previewOpen, setPreviewOpen] = useState(false);
  // FSA-2.4.4 — Focus mode (overlay the screen over the app sidebar/bar for a
  // max-area review pass) + Deviations drawer (validations live in a right
  // drawer behind a toolbar chip instead of a top band).
  const [focusMode, setFocusMode] = useState(false);
  const [deviationsDrawerOpen, setDeviationsDrawerOpen] = useState(false);
  // Esc closes the deviations drawer first, then exits focus mode.
  useEffect(() => {
    if (!focusMode && !deviationsDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (deviationsDrawerOpen) setDeviationsDrawerOpen(false);
      else if (focusMode) setFocusMode(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusMode, deviationsDrawerOpen]);

  // Save / Submit / Approve / Reopen
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [transitionConfirm, setTransitionConfirm] = useState<{
    open: boolean;
    kind: 'submit' | 'approve' | 'reopen';
    /** TRUE when the statement differed from the latest Notes at click time. */
    withLiveDrift?: boolean;
  }>({ open: false, kind: 'submit' });
  const [isWorking, setIsWorking] = useState(false);

  // FSA-2.2 — Include / Exclude (only the Cash Flow tab surfaces this toggle).
  const [excludeConfirmOpen, setExcludeConfirmOpen] = useState(false);
  const [clearAndExcludeConfirmOpen, setClearAndExcludeConfirmOpen] = useState(false);

  const status = hydrate?.workspace?.statusCode ?? 'DRAFT';
  const isLocked = status === 'SUBMITTED' || status === 'APPROVED';
  // FSA — the Cash Flow Statement defaults to NOT APPLICABLE: most small /
  // non-corporate engagements don't prepare one, so the auditor opts IN via
  // "Mark Applicable" when required rather than marking it Not Applicable
  // every time. Once the auditor makes any explicit choice it is persisted on
  // the workspace (isExcluded) and wins. All other statements default to
  // applicable.
  const isExcluded = hydrate?.workspace?.isExcluded ?? (reportCode === 'CF');

  // FSA-2.4 S3-B/S3-C — Live drift detector. Used by Save (auto-applies
  // refresh) and Submit (blocks until drift = 0). Walks every layout
  // line, ignores overrides (auditor intent), and reports whether any
  // saved value differs from the latest live evaluation. Mirrors the
  // per-row drift check in LayoutRow so the two surfaces always agree.
  const hasLiveDrift = useMemo(() => {
    if (!layout?.lines || !hydrate?.liveEvaluation) return false;
    for (const line of layout.lines) {
      if (line.kind === 'SECTION_HEADER' || !line.code) continue;
      const saved = workspace.lines[line.code];
      const live = hydrate.liveEvaluation[line.code];
      if (!live || live.isReference) continue;
      if (saved?.is_override) continue; // override preserved through refresh
      if (!saved) continue;
      const a = Math.abs((saved.amount_actual ?? 0) - (live.amountActual ?? 0));
      const p = Math.abs((saved.prior_amount_actual ?? 0) - (live.priorAmountActual ?? 0));
      if (a > 0.005 || p > 0.005) return true;
    }
    return false;
  }, [layout?.lines, hydrate?.liveEvaluation, workspace.lines]);

  const handleConfirmSave = useCallback(async () => {
    if (!hydrate) return;
    setSaveConfirmOpen(false);
    setIsWorking(true);
    try {
      await fsaStatementsService.save(engagementId, reportId, {
        rowVersion: hydrate.workspace?.rowVersion ?? null,
        data: JSON.stringify(workspace),
      });
      notificationService.toast.success(`${reportName} saved.`);
      // Snapshot via canonical stringify so the post-save dirty check
      // matches whatever JSONB key order the server hands back on the
      // imminent refetch below. Without this, the refetch's useEffect
      // would rebuild savedSnapshot in JSONB order, the in-flight
      // workspace still in evaluatedToLine order, and isDirty would
      // re-fire even though nothing actually changed.
      initialSnapshotRef.current = canonicalStringify(workspace);
      // Clear the per-row unsaved highlight immediately rather than waiting
      // for the refetch below to round-trip — what we just persisted IS the
      // saved state.
      setSavedLines(workspace.lines);
      refetch();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      notificationService.toast.error(msg || 'Save failed.');
    } finally {
      setIsWorking(false);
    }
  }, [engagementId, reportId, reportName, hydrate, workspace, refetch]);

  const handleConfirmTransition = useCallback(async () => {
    const kind = transitionConfirm.kind;
    setTransitionConfirm(prev => ({ ...prev, open: false }));
    if (!hydrate) return;
    setIsWorking(true);
    try {
      const rowVersion = hydrate.workspace?.rowVersion ?? null;
      const fn =
        kind === 'submit' ? fsaStatementsService.submit :
        kind === 'approve' ? fsaStatementsService.approve :
        fsaStatementsService.reopen;
      await fn.call(fsaStatementsService, engagementId, reportId, { rowVersion });
      notificationService.toast.success(
        kind === 'submit' ? `${reportName} submitted for review.` :
        kind === 'approve' ? `${reportName} approved.` :
        `${reportName} reopened to draft.`
      );
      refetch();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      notificationService.toast.error(msg || 'Transition failed.');
    } finally {
      setIsWorking(false);
    }
  }, [engagementId, reportId, reportName, hydrate, transitionConfirm.kind, refetch]);

  // FSA-2.2 — perform the actual exclude call (clearSavedData flag flips
  // between the pre-confirm and the second "clear saved data?" confirm).
  // On 409 with `requires_clear_confirmation` we surface the second
  // ConfirmDialog and retry with the flag.
  const performExclude = useCallback(async (clearSavedData: boolean) => {
    setIsWorking(true);
    try {
      await fsaStatementsService.exclude(engagementId, reportId, { clearSavedData });
      notificationService.toast.success(`${reportName} marked Not Applicable.`);
      setExcludeConfirmOpen(false);
      setClearAndExcludeConfirmOpen(false);
      refetch();
    } catch (err: any) {
      const status = err?.response?.status;
      const errors: string[] = err?.response?.data?.errors ?? [];
      if (status === 409 && errors.includes('requires_clear_confirmation')) {
        setExcludeConfirmOpen(false);
        setClearAndExcludeConfirmOpen(true);
      } else {
        notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to exclude statement.');
      }
    } finally {
      setIsWorking(false);
    }
  }, [engagementId, reportId, reportName, refetch]);

  const handleInclude = useCallback(async () => {
    setIsWorking(true);
    try {
      await fsaStatementsService.include(engagementId, reportId);
      notificationService.toast.success(`${reportName} marked Applicable.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to reinstate statement.');
    } finally {
      setIsWorking(false);
    }
  }, [engagementId, reportId, reportName, refetch]);

  // BS reconciliation — only meaningful when this report is the
  // Balance Sheet. Look up the two TOTAL-kind lines defined in the
  // Schedule III seed (`BS_TOTAL_EQUITY_LIABILITIES` and
  // `BS_TOTAL_ASSETS`). When they match within rounding tolerance the
  // statement is reconciled; otherwise we surface the absolute drift
  // so the auditor sees the gap at a glance.
  //
  // Hook order: this `useMemo` runs every render so it must live
  // ABOVE the loading/error early returns — otherwise the hook count
  // would change between the loading render (no hook) and the loaded
  // render (one hook), violating the Rules of Hooks.
  const isBalanceSheet = reportCode === 'BS';
  const isCashFlow = reportCode === 'CF';
  // FSA-2.4 FIX — Reconciliation must consider BOTH CY and PY. Reading
  // only CY (the prior behaviour) mislabelled a BS with reconciled CY
  // but drifted PY as "Reconciled". Now reconciled iff both periods
  // tally within tolerance; expose both diffs so the deviation entry
  // and header pill can describe whichever is drifted.
  const bsReconciliation = useMemo(() => {
    if (!isBalanceSheet) return null;
    const eq = workspace.lines['BS_TOTAL_EQUITY_LIABILITIES'];
    const as = workspace.lines['BS_TOTAL_ASSETS'];
    if (!eq || !as) return null;
    const eqValCy = displayMode === 'rounded' ? eq.amount_rounded : eq.amount_actual;
    const asValCy = displayMode === 'rounded' ? as.amount_rounded : as.amount_actual;
    const eqValPy = displayMode === 'rounded' ? eq.prior_amount_rounded : eq.prior_amount_actual;
    const asValPy = displayMode === 'rounded' ? as.prior_amount_rounded : as.prior_amount_actual;
    const diffCy = Math.abs((eqValCy ?? 0) - (asValCy ?? 0));
    const diffPy = Math.abs((eqValPy ?? 0) - (asValPy ?? 0));
    const cyOk = diffCy <= 0.5;
    const pyOk = diffPy <= 0.5;
    return {
      reconciled: cyOk && pyOk,
      diff: Math.max(diffCy, diffPy),
      diffCy,
      diffPy,
      cyOk,
      pyOk,
    };
  }, [isBalanceSheet, workspace.lines, displayMode]);

  // FSA-2.4 S1-B — Deviations aggregator for the Statement.
  // Walks every layout line + workspace + liveEvaluation pair, counts
  // drift / TB-drift / non-final source notes / overrides. Drives the
  // single Deviations panel at the top of the body so the auditor
  // doesn't have to scan the table for exceptions. Auto-hides to
  // `✓ All checks passed` when nothing fires.
  const deviationItems = useMemo<NoteDeviationItem[]>(() => {
    const out: NoteDeviationItem[] = [];
    if (!layout?.lines || !hydrate?.liveEvaluation) return out;
    // FSA-2.4 FIX — When the Statement is marked Not Applicable (e.g.
    // Cash Flow with clearSavedData=true), the saved workspace `data`
    // payload comes back as `{}` (no `lines` property). Skip the
    // exception walk entirely when excluded — the body renders an
    // excluded placeholder anyway. Also defensively coerce `lines` so
    // any future shape-change doesn't crash the unconditional memo.
    if (hydrate?.workspace?.isExcluded) return out;
    let ws: StatementWorkspaceData = { lines: {} };
    if (hydrate.workspace?.data) {
      try {
        const parsed = JSON.parse(hydrate.workspace.data) as StatementWorkspaceData | null;
        ws = { lines: parsed?.lines ?? {}, subsections: parsed?.subsections ?? undefined };
      } catch { /* unparseable — treat as empty */ }
    }
    let driftCount = 0, tbDriftCount = 0;
    let nonFinalSourceCount = 0;
    let overrideCount = 0;
    for (const line of layout.lines) {
      if (line.kind === 'SECTION_HEADER' || !line.code) continue;
      const saved = ws.lines[line.code];
      const live = hydrate.liveEvaluation[line.code];
      if (!live || live.isReference) continue;

      if (saved != null) {
        const a = Math.abs((saved.amount_actual ?? 0) - (live.amountActual ?? 0));
        const p = Math.abs((saved.prior_amount_actual ?? 0) - (live.priorAmountActual ?? 0));
        if (a > 0.005 || p > 0.005) driftCount++;
        if (saved.is_override) overrideCount++;
      }
      if (line.kind === 'NOTE_TOTAL'
          && live.liveTbClosingActual != null
          && Math.abs((live.amountActual ?? 0) - (live.liveTbClosingActual ?? 0)) > 0.005) {
        tbDriftCount++;
      }
      if (line.kind === 'NOTE_TOTAL' && live.sourceStatusCode && live.sourceStatusCode !== 'APPROVED') {
        nonFinalSourceCount++;
      }
    }

    // BS drift — error severity, highest priority. Surfaces in the
    // panel even though the summary strip also shows it (panel is the
    // "what needs my action" view; strip is the "what does the
    // statement look like" view).
    if (isBalanceSheet && bsReconciliation && !bsReconciliation.reconciled) {
      // FSA-2.4 FIX — Detail now spells out which period(s) drift so
      // the auditor knows where to look. "Both" means CY + PY off
      // simultaneously (rare but possible after a regroup).
      const { diffCy, diffPy, cyOk, pyOk } = bsReconciliation;
      const detail = (!cyOk && !pyOk)
        ? `Total Assets ≠ Total Equity + Liab: CY drift ${fmt(diffCy)}, PY drift ${fmt(diffPy)}`
        : !cyOk
          ? `Total Assets ≠ Total Equity + Liab on CY (drift ${fmt(diffCy)})`
          : `Total Assets ≠ Total Equity + Liab on PY (drift ${fmt(diffPy)})`;
      out.push({
        id: 'bs-drift',
        severity: 'error',
        label: 'Balance Sheet does not reconcile',
        detail,
      });
    }

    if (driftCount > 0) {
      out.push({
        id: 'line-drift',
        severity: 'warning',
        label: `${driftCount} line${driftCount === 1 ? '' : 's'} differ from the latest Notes`,
        detail: 'Click Save to pull and persist the latest values',
      });
    }

    if (tbDriftCount > 0) {
      out.push({
        id: 'tb-drift',
        severity: 'warning',
        label: `${tbDriftCount} source Note${tbDriftCount === 1 ? '' : 's'} stale vs trial balance`,
        detail: 'Open the Note(s) and Save to refresh from live TB',
      });
    }

    if (nonFinalSourceCount > 0) {
      out.push({
        id: 'non-final-sources',
        severity: 'info',
        label: `${nonFinalSourceCount} source Note${nonFinalSourceCount === 1 ? '' : 's'} not yet finalised`,
        detail: 'DRAFT / SUBMITTED notes — review status on each line',
      });
    }

    if (overrideCount > 0) {
      out.push({
        id: 'overrides',
        severity: 'info',
        label: `${overrideCount} line${overrideCount === 1 ? '' : 's'} with auditor override`,
        detail: 'Highlighted in the table with an override marker',
      });
    }

    // FSA-2.4 CF6 — Cash Flow-specific deviations.
    if (isCashFlow) {
      // Cash reconciliation: |CF_CLOSING_CASH − NOTE_CASH_EQUIVALENTS (CY closing)| ≤ 0.005.
      const closingCashLive   = hydrate.liveEvaluation['CF_CLOSING_CASH'];
      const cashNoteLive      = hydrate.liveEvaluation['BS_CASH_EQUIVALENTS'];
      if (closingCashLive && cashNoteLive) {
        const drift = Math.abs((closingCashLive.amountActual ?? 0) - (cashNoteLive.amountActual ?? 0));
        if (drift > 0.005) {
          out.push({
            id: 'cf-cash-recon',
            severity: 'error',
            label: 'Cash Flow closing cash does not match BS Cash & Cash Equivalents',
            detail: `Drift ₹ ${fmt(drift)} — review operating / investing / financing totals or the cash note.`,
          });
        }
      }

      // Missing PY — count CF items/lines with PY actual = 0.
      let missingPyItems = 0, missingPyLines = 0;
      const VALUE_CODES = ['CF_OP_PBT', 'CF_OP_TAX_PAID', 'CF_OPENING_CASH'];
      for (const code of VALUE_CODES) {
        const line = ws.lines[code];
        if (!line || (line.prior_amount_actual ?? 0) === 0) missingPyLines++;
      }
      const subsections = (ws as StatementWorkspaceData).subsections ?? {};
      for (const sub of Object.values(subsections)) {
        for (const item of (sub?.items ?? [])) {
          if ((item.prior_amount_actual ?? 0) === 0) missingPyItems++;
        }
      }
      const missingTotal = missingPyLines + missingPyItems;
      if (missingTotal > 0) {
        out.push({
          id: 'cf-missing-py',
          severity: 'info',
          label: `${missingTotal} CF entr${missingTotal === 1 ? 'y has' : 'ies have'} no Prior-Year value`,
          detail: 'Click "Import PY from Prior CF" in the toolbar, or open each subsection / row to type PY manually.',
        });
      }

      // Manual entry required — items with no value_source AND amount = 0
      // (auditor likely hasn't filled them yet).
      let blankManual = 0;
      for (const sub of Object.values(subsections)) {
        for (const item of (sub?.items ?? [])) {
          if (!item.value_source && (item.amount_actual ?? 0) === 0 && !item.is_override) {
            blankManual++;
          }
        }
      }
      if (blankManual > 0) {
        out.push({
          id: 'cf-blank-manual',
          severity: 'info',
          label: `${blankManual} manual CF item${blankManual === 1 ? '' : 's'} still blank`,
          detail: 'Open the relevant subsection and fill in (e.g. Tax Paid, Interest Paid, Dividend, etc.).',
        });
      }
    }

    return out;
  }, [layout?.lines, hydrate?.liveEvaluation, hydrate?.workspace, isBalanceSheet, bsReconciliation, isCashFlow]);

  // FSA-2.4 CF — client-side re-evaluation of every layout line's
  // displayed amounts, derived from the current workspace state. Mirror
  // of the backend evaluator's two-pass walk so SUBTOTAL / TOTAL lines
  // reflect modal-time edits to subsections immediately (before the
  // user clicks Save on the Statement).
  //
  // Source preference per line:
  //   • SUBSECTION: Σ workspace.subsections[code].items (auditor's edits)
  //                 → fallback to liveEvaluation.subsectionItems (seeds)
  //   • VALUE / NOTE_TOTAL: workspace.lines[code] → fallback liveEvaluation
  //   • SUBTOTAL / TOTAL: evaluate formula referencing previously-resolved codes
  //
  // LayoutRow uses this map for SUBSECTION + SUBTOTAL + TOTAL rendering;
  // VALUE / NOTE_TOTAL keep their existing source priority (saved-wins-
  // for-non-overrides + live-by-default in DRAFT).
  const liveDisplayValues = useMemo(() => {
    const map = new Map<string, { cy: number; cyR: number; py: number; pyR: number }>();
    if (!layout?.lines) return map;
    const liveEv = hydrate?.liveEvaluation ?? {};

    // Pass 1 — value-producing lines.
    for (const ln of layout.lines) {
      if (!ln.code) continue;
      if (ln.kind === 'SUBSECTION') {
        const savedItems = workspace.subsections?.[ln.code]?.items ?? [];
        const liveItems  = liveEv[ln.code]?.subsectionItems ?? [];
        // Saved wins when present (auditor has edited via modal); else seeds.
        const cyAct = savedItems.length > 0
          ? savedItems.reduce((s, i) => s + (i.amount_actual ?? 0), 0)
          : liveItems.reduce((s, i) => s + (i.amountActual ?? 0), 0);
        const cyR = savedItems.length > 0
          ? savedItems.reduce((s, i) => s + (i.amount_rounded ?? 0), 0)
          : liveItems.reduce((s, i) => s + (i.amountRounded ?? 0), 0);
        const pyAct = savedItems.length > 0
          ? savedItems.reduce((s, i) => s + (i.prior_amount_actual ?? 0), 0)
          : liveItems.reduce((s, i) => s + (i.priorAmountActual ?? 0), 0);
        const pyR = savedItems.length > 0
          ? savedItems.reduce((s, i) => s + (i.prior_amount_rounded ?? 0), 0)
          : liveItems.reduce((s, i) => s + (i.priorAmountRounded ?? 0), 0);
        map.set(ln.code, { cy: cyAct, cyR, py: pyAct, pyR });
      } else if (ln.kind === 'VALUE' || ln.kind === 'NOTE_TOTAL') {
        const saved = workspace.lines[ln.code];
        const live  = liveEv[ln.code];
        map.set(ln.code, {
          cy:  saved?.amount_actual        ?? live?.amountActual        ?? 0,
          cyR: saved?.amount_rounded       ?? live?.amountRounded       ?? 0,
          py:  saved?.prior_amount_actual  ?? live?.priorAmountActual   ?? 0,
          pyR: saved?.prior_amount_rounded ?? live?.priorAmountRounded  ?? 0,
        });
      }
    }

    // Pass 2 — SUBTOTAL / TOTAL formulas. Linear "+" / "-" tokens
    // referencing codes already resolved in pass 1. Unknown tokens
    // contribute 0 (matches backend behaviour).
    for (const ln of layout.lines) {
      if (!ln.code || !ln.formula) continue;
      if (ln.kind !== 'SUBTOTAL' && ln.kind !== 'TOTAL') continue;
      let cy = 0, cyR = 0, py = 0, pyR = 0;
      let sign = 1;
      for (const raw of ln.formula.split(/\s+/)) {
        const t = raw.trim();
        if (!t) continue;
        if (t === '+') { sign = 1; continue; }
        if (t === '-') { sign = -1; continue; }
        const v = map.get(t);
        if (v) {
          cy  += sign * v.cy;
          cyR += sign * v.cyR;
          py  += sign * v.py;
          pyR += sign * v.pyR;
        }
        sign = 1;
      }
      map.set(ln.code, { cy, cyR, py, pyR });
    }

    return map;
  }, [layout?.lines, hydrate?.liveEvaluation, workspace.lines, workspace.subsections]);

  // FSA-2.4 S1-A — Statement summary strip data.
  // BS shows Total Assets / Total Equity+Liab / Drift; PL shows Total
  // Income / Total Expenses / Profit (Loss); CF skips for now (no
  // canonical "summary trio" — section subtotals work but the strip
  // would carry less load). Mode-aware: actual vs rounded picks the
  // matching slot from the saved workspace.
  const statementSummary = useMemo<{
    title: string;
    /**
     * Each item has both a full label (for tooltip / aria) and a short
     * label (TA / TE+L / Drift / Income / etc.) for the compact inline
     * row layout. With period as the row label and short labels on each
     * value, the strip body fits in two rows without a column-header
     * row above (FSA-2.4.3).
     */
    items: Array<{
      label: string;
      shortLabel: string;
      cy: number;
      py: number;
      emphasis?: 'positive' | 'negative' | 'neutral';
      isDrift?: boolean;
      /**
       * Profit/(Loss) row — the label, emphasis, and amount sign are resolved
       * PER PERIOD from the signed `cy`/`py` value at render time (positive →
       * Profit, negative → Loss). For these items `cy`/`py` are stored SIGNED
       * (not pre-abs'd) so each period reflects its own result; the display
       * shows the magnitude under the period-correct label.
       */
      isProfitLoss?: boolean;
    }>;
    /**
     * FSA-2.4 — BS header drift state. `cy` / `py` carry each
     * period's drift magnitude separately so the header pill can read
     * "Reconciled" only when BOTH are within tolerance — any drift on
     * either side switches it to "Out of Balance" with the worst
     * (larger) amount surfaced for at-a-glance read.
     */
    driftDiff?: { cy: number; py: number };
  } | null>(() => {
    const get = (code: string) => {
      const row = workspace.lines[code];
      if (!row) return null;
      const cy = displayMode === 'rounded' ? row.amount_rounded : row.amount_actual;
      const py = displayMode === 'rounded' ? row.prior_amount_rounded : row.prior_amount_actual;
      return { cy: cy ?? 0, py: py ?? 0 };
    };
    if (isBalanceSheet) {
      const assets = get('BS_TOTAL_ASSETS');
      const eql    = get('BS_TOTAL_EQUITY_LIABILITIES');
      if (!assets || !eql) return null;
      const driftCy = Math.abs(eql.cy - assets.cy);
      const driftPy = Math.abs(eql.py - assets.py);
      return {
        title: 'Balance Sheet Reconciliation',
        items: [
          { label: 'Total Assets',         shortLabel: 'TA',    cy: assets.cy, py: assets.py },
          { label: 'Total Equity + Liab.', shortLabel: 'TE+L',  cy: eql.cy,    py: eql.py },
          { label: 'Drift',                shortLabel: 'Drift', cy: driftCy,   py: driftPy, isDrift: true },
        ],
        driftDiff: { cy: driftCy, py: driftPy },
      };
    }
    if (reportCode === 'PL') {
      const income  = get('PL_TOTAL_INCOME');
      const expense = get('PL_TOTAL_EXPENSES');
      const profit  = get('PL_PROFIT_FOR_PERIOD');
      if (!income || !expense || !profit) return null;
      // FSA-2.4 FIX (2026-06-22): PL_PROFIT_FOR_PERIOD is evaluator-
      // computed as `Total Income − Total Expenses − Tax` per the
      // SUBTOTAL formula chain in the seed. The evaluator already
      // sign-flips income from its credit-balance source (negative
      // stored → positive displayed). So at this point:
      //   PROFIT (income > expense) → stored POSITIVE
      //   LOSS   (income < expense) → stored NEGATIVE
      // The previous `-profit.cy` negation inverted the sign-test and
      // mislabelled every profit as a loss. Drop the negation; sign
      // drives the label directly.
      //
      // Each period's Profit/(Loss) label + sign is resolved INDEPENDENTLY
      // from its own signed value at render time — a Profit CY alongside a
      // Loss PY (or vice versa) must each read correctly. Store the signed
      // values; the render derives label/emphasis/magnitude per period.
      return {
        title: 'Profit & Loss Summary',
        items: [
          { label: 'Total Income',   shortLabel: 'Income',   cy: Math.abs(income.cy),  py: Math.abs(income.py) },
          { label: 'Total Expenses', shortLabel: 'Expenses', cy: Math.abs(expense.cy), py: Math.abs(expense.py) },
          {
            label: 'Profit / (Loss) for the Period',
            shortLabel: 'Profit / (Loss)',
            cy: profit.cy,   // SIGNED — see isProfitLoss
            py: profit.py,   // SIGNED
            isProfitLoss: true,
          },
        ],
      };
    }
    return null;
  }, [isBalanceSheet, reportCode, workspace.lines, displayMode]);

  if (loading && !hydrate) {
    return <div style={loaderStyle}>Loading {reportName}…</div>;
  }
  if (error) {
    return <div style={errorStyle}>Error loading {reportName}: {error.message}</div>;
  }
  if (!hydrate || !layout) {
    return <div style={loaderStyle}>{reportName} layout not available.</div>;
  }

  // Paginate the layout. Section headers split alongside their lines —
  // we don't try to keep a header pinned with its body across pages
  // (the auditor knows the layout). `totalPages` is at least 1 so the
  // pagination row always renders something useful.
  const allLines = layout.lines ?? [];
  // Deviations chip summary — error/warning items are real problems; info
  // items are an audit-trail breakdown shown in the drawer, not counted here.
  const problemDeviations = deviationItems.filter(d => d.severity === 'error' || d.severity === 'warning');
  const deviationCount = problemDeviations.length;
  const deviationHasError = problemDeviations.some(d => d.severity === 'error');

  // FSA-2.4.4 — Totals/balance bar. Moved OUT of the top chrome to a sticky
  // bar pinned at the FOOT of the statement: Total Assets / Total Equity &
  // Liabilities / drift (BS) — or Income / Expenses / Profit (PL) — stay
  // visible without scrolling to the end, while the top stays lean so more
  // statement lines fit. Collapsed by default; the drift / out-of-balance
  // status shows in the bar header even when collapsed.
  const summaryBar = (!isExcluded && statementSummary) ? (() => {
    const headerDriftDiff = statementSummary.driftDiff;
    const cyDrifted = headerDriftDiff !== undefined && headerDriftDiff.cy > 0.5;
    const pyDrifted = headerDriftDiff !== undefined && headerDriftDiff.py > 0.5;
    const anyDrifted = cyDrifted || pyDrifted;
    const headerDriftLabel = !headerDriftDiff
      ? null
      : anyDrifted
        ? (cyDrifted && pyDrifted
            ? `⚠ Out of Balance CY ₹${fmt(headerDriftDiff.cy)} / PY ₹${fmt(headerDriftDiff.py)}`
            : cyDrifted
              ? `⚠ Out of Balance ₹${fmt(headerDriftDiff.cy)} (CY)`
              : `⚠ Out of Balance ₹${fmt(headerDriftDiff.py)} (PY)`)
        : '✓ Reconciled';
    return (
      <div style={{ ...summaryStripStyle, margin: 0, borderRadius: 0, borderTop: '1px solid var(--border-color)' }}>
        <button
          type="button"
          onClick={() => setSummaryStripCollapsed(prev => !prev)}
          style={summaryStripHeaderButtonStyle}
          aria-expanded={!summaryStripCollapsed}
        >
          <span style={summaryStripHeaderLeftStyle}>
            <span style={summaryStripChevronStyle(!summaryStripCollapsed)}>▾</span>
            <span>{statementSummary.title}</span>
          </span>
          {headerDriftLabel && (
            <span style={summaryStripHeaderDriftStyle(!anyDrifted)}>{headerDriftLabel}</span>
          )}
        </button>
        {!summaryStripCollapsed && (
          <div style={summaryStripBodyStyle}>
            {(['cy', 'py'] as const).map(period => (
              <div key={period} style={summaryStripPeriodRowStyle}>
                <span style={summaryStripPeriodAnchorStyle}>{period.toUpperCase()}</span>
                <span style={summaryStripPeriodValuesStyle}>
                  {statementSummary.items.map(item => {
                    const rawValue = period === 'cy' ? item.cy : item.py;
                    // Profit/(Loss) rows resolve label + emphasis + sign from
                    // THIS period's value (positive → Profit, negative → Loss);
                    // other rows use the pre-computed label/value as-is.
                    const isProfit = rawValue >= 0;
                    const shortLabel = item.isProfitLoss ? (isProfit ? 'Profit' : 'Loss') : item.shortLabel;
                    const fullLabel = item.isProfitLoss
                      ? (isProfit ? 'Profit for the Period' : 'Loss for the Period')
                      : item.label;
                    const emphasis = item.isProfitLoss ? (isProfit ? 'positive' : 'negative') : item.emphasis;
                    const value = item.isProfitLoss ? Math.abs(rawValue) : rawValue;
                    return (
                      <span
                        key={item.label}
                        style={summaryStripInlineValueStyle(item.isDrift, emphasis)}
                        title={fullLabel}
                      >
                        <span style={summaryStripShortLabelStyle}>{shortLabel}</span>
                        <span style={summaryStripAmountStyle}>₹ {fmt(value)}</span>
                      </span>
                    );
                  })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  })() : null;

  return (
    <div style={focusMode ? { ...screenStyle, ...focusOverlayStyle } : screenStyle}>
      {/* FSA-2.4.2 — Header restructured for vertical-space efficiency.
          Top row: title only (left) + action buttons (right).
          Subtitle: report code + last-saved on a tight line.
          Bottom row: status / dirty / recon chips + display toggle.
          Badges previously shared a row with the title and could wrap
          on narrow viewports; isolating them recovers the title row's
          full width for actions. */}
      <div style={headerStyle}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={headerTitleStyle}>
            <strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{reportName}</strong>
          </div>
          {/* FSA-2.4.4 — status / dirty / deviation chips + meta on the
              subtitle line, so the title keeps its full row (no "Balan…"
              truncation) and the line reads as one compact status strip. */}
          <div style={{ ...headerSubtitleStyle, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {isExcluded
              ? <span style={excludedChipStyle} title="This Statement is excluded from the consolidated FS PDF for this engagement.">✗ Not Applicable</span>
              : <span style={statusChipStyleFor(status)}>● {status}</span>}
            {isDirty && !isExcluded && <span style={dirtyChipStyle}>unsaved</span>}
            {hasLiveDrift && !isExcluded && (
              <span style={dirtyChipStyle} title="One or more Statement lines differ from the latest Notes. Click Save to refresh and persist.">live drift</span>
            )}
            {!isExcluded && (
              <button
                type="button"
                onClick={() => setDeviationsDrawerOpen(true)}
                title="Open validations & deviations"
                style={{
                  padding: '1px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700,
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: deviationCount === 0 ? '#dcfce7' : deviationHasError ? '#fee2e2' : '#fef3c7',
                  color: deviationCount === 0 ? '#166534' : deviationHasError ? '#991b1b' : '#92400e',
                }}
              >
                {deviationCount === 0 ? '✓ Checks passed' : `⚠ ${deviationCount} deviation${deviationCount > 1 ? 's' : ''}`}
              </button>
            )}
            <span style={{ color: 'var(--text-secondary)' }}>
              {reportCode}
              {hydrate.workspace?.updatedAt && <> · saved {formatDateTime(hydrate.workspace.updatedAt)}</>}
            </span>
          </div>
        </div>
        <div style={headerActionsStyle}>
          {/* FSA-2.4.4 — Focus mode: overlay the statement for a max-area review pass. */}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setFocusMode(f => !f)}
            title={focusMode ? 'Exit focus mode' : 'Focus mode — maximise the review area'}
            aria-pressed={focusMode}
          >
            <IconSystem.eye size={13} /> {focusMode ? 'Exit Focus' : 'Focus'}
          </button>
          {/* FSA-2.4.4 — Actual/Rounded toggle moved into the toolbar. */}
          <span style={displayToggleWrapStyle} role="group" aria-label="Display mode">
            <button
              type="button"
              style={displayToggleBtnStyle(displayMode === 'actual')}
              onClick={() => setDisplayMode('actual')}
              title="Show values in their actual (un-rounded) form"
            >
              Actual
            </button>
            <button
              type="button"
              style={displayToggleBtnStyle(displayMode === 'rounded')}
              onClick={() => setDisplayMode('rounded')}
              title="Show values rounded per the engagement's rounding factor"
            >
              Rounded
            </button>
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleRefreshAll}
            disabled={isLocked || isWorking || !hydrate.liveEvaluation}
            title="Pull every line's value from the live evaluation (saved Notes + computed formulas). Auditor overrides are reset."
          >
            <IconSystem.refresh size={13} />
          </button>
          {isCashFlow && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setImportPriorPyConfirmOpen(true)}
              disabled={isLocked || isWorking}
              title="Bulk-populate every PY cell on this Cash Flow from the prior engagement's saved CF. Requires a prior engagement linked in FSA settings."
            >
              <IconSystem.download size={13} />
            </button>
          )}
          {/* CF Mark (Not) Applicable — placed before Save per auditor request. */}
          {isCashFlow && (
            isExcluded ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleInclude}
                disabled={isWorking}
                title="Reinstate the Cash Flow Statement for this engagement. The consolidated FS PDF will include it again."
              >
                <IconSystem.check size={13} />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setExcludeConfirmOpen(true)}
                disabled={isWorking || isLocked}
                title={isLocked
                  ? 'Reopen the Statement before marking Not Applicable.'
                  : 'Mark the Cash Flow Statement Not Applicable for this engagement. Skipped from the consolidated FS PDF.'}
              >
                <IconSystem.x size={13} />
              </button>
            )
          )}
          {status === 'DRAFT' && (
            <>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={async () => {
                  // FSA-2.4 S3-B — Save auto-applies refresh on click.
                  // If live drift exists, pull the latest evaluation
                  // (preserving overrides) BEFORE opening the save
                  // confirm. The auditor sees one click = one save of
                  // what the cells display (live values with amber tint).
                  if (hasLiveDrift) {
                    await handleRefreshAll();
                  }
                  setSaveConfirmOpen(true);
                }}
                disabled={isWorking || (!isDirty && !hasLiveDrift && !!hydrate.workspace)}
                title={
                  hasLiveDrift
                    ? 'Refresh from Notes and save in one step'
                    : !isDirty && hydrate.workspace
                    ? 'No changes to save'
                    : hydrate.workspace
                    ? 'Persist the current values to tbl_engagement_statement_data'
                    : 'Save the initial snapshot for this Statement'
                }
              >
                {isWorking ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  // Drift against the latest Notes no longer REFUSES the
                  // submit — the auditor is the final authority and may
                  // knowingly submit a statement that differs from the
                  // Notes. It is surfaced in the confirmation instead, so
                  // the decision is deliberate rather than accidental.
                  setTransitionConfirm({ open: true, kind: 'submit', withLiveDrift: hasLiveDrift });
                }}
                disabled={isWorking || isDirty || !hydrate.workspace}
                title={
                  hasLiveDrift
                    ? 'Cannot submit — line drift exists. Save first.'
                    : isDirty
                    ? 'Save before submitting'
                    : !hydrate.workspace
                    ? 'Save at least once before submitting'
                    : 'Submit for review'
                }
              >
                Submit
              </button>
            </>
          )}
          {status === 'SUBMITTED' && (
            <>
              <button type="button" className="btn btn-primary btn-sm"
                onClick={() => setTransitionConfirm({ open: true, kind: 'approve' })} disabled={isWorking}>
                Approve
              </button>
              <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => setTransitionConfirm({ open: true, kind: 'reopen' })} disabled={isWorking}>
                Reopen
              </button>
            </>
          )}
          {status === 'APPROVED' && (
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={() => setTransitionConfirm({ open: true, kind: 'reopen' })} disabled={isWorking}>
              Reopen
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              if (!hydrate?.workspace) {
                notificationService.toast.warning('Save the Statement at least once before previewing.');
                return;
              }
              setPreviewOpen(true);
            }}
            disabled={isWorking || !hydrate?.workspace}
            title={
              hydrate?.workspace
                ? 'Open the server-rendered Statement PDF in a preview viewer.'
                : 'Save the Statement before previewing.'
            }
          >
            <IconSystem.file size={13} /> Preview
          </button>
        </div>
      </div>




      {/* Excluded placeholder — replaces the layout table when the Cash
          Flow Statement is marked Not Applicable. Header chrome stays
          rendered so the auditor can flip back via "Mark Applicable". */}
      {isExcluded ? (
        <div style={excludedPlaceholderStyle}>
          <IconSystem.x size={36} />
          <div style={excludedPlaceholderTitleStyle}>{reportName} marked Not Applicable</div>
          <div style={excludedPlaceholderSubtitleStyle}>
            This Statement is skipped from the consolidated FS PDF for this engagement.
            Use <strong>Mark Applicable</strong> in the toolbar to reinstate it.
          </div>
        </div>
      ) : (
      /* Layout table — borrows SyutGrid's visual chrome (sticky thead
          with #dbeafe header bg, border-collapse, font sizing) AND
          its pagination footer chrome (`.pagination-footer` +
          `.page-btn`). We don't use the SyutGrid component itself
          because the layout has kind-specific row shapes (SECTION_HEADER
          spans 4 columns, SUBTOTAL rows are bold) that don't fit
          SyutGrid's uniform-column model. */
      <div
        className="syut-grid-component"
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={tableScrollStyle}>
          <table style={tableStyle}>
            <colgroup>
              <col style={{ width: 'auto' }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 60 }} />
            </colgroup>
            <thead style={theadStyle}>
              <tr>
                <th style={thLabelStyle}>Particulars</th>
                <th style={thNoteStyle}>Note No.</th>
                <th style={thAmountStyle}>{displayMode === 'rounded' ? 'CY Rounded' : 'CY Actual'}</th>
                <th style={thAmountStyle}>{displayMode === 'rounded' ? 'PY Rounded' : 'PY Actual'}</th>
                <th style={thAmountStyle}></th>
              </tr>
            </thead>
            <tbody>
              {allLines.map((line, idx) => (
                <LayoutRow
                  key={line.code ?? `header-${idx}`}
                  line={line}
                  workspace={workspace}
                  savedLines={savedLines}
                  liveEvaluation={hydrate.liveEvaluation}
                  liveDisplayValues={liveDisplayValues}
                  displayMode={displayMode}
                  onRefreshLine={handleRefreshLine}
                  onNavigateToNote={handleNavigateToNote}
                  onOpenSubsection={handleOpenSubsection}
                  isLive={!isLocked}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* FSA-2.4.4 — Sticky totals / balance bar pinned at the foot of the
            statement (replaces the old pagination footer — statements render
            in full now). Total Assets / Total Equity & Liabilities / drift
            stay visible without scrolling to the end. */}
        {summaryBar}
      </div>
      )}

      {/* FSA-2.4.4 — Deviations / validations drawer. Rendered INSIDE the
          screen root (not portaled) so it stays visible in focus mode (a
          body portal would sit outside the focus overlay). Slides in from
          the right; reuses NoteDeviationPanel expanded. */}
      {deviationsDrawerOpen && (
        <>
          <div
            onClick={() => setDeviationsDrawerOpen(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.28)', zIndex: 45 }}
          />
          <div
            style={{
              position: 'absolute', top: 0, right: 0, bottom: 0,
              width: 'min(420px, 92%)', background: 'var(--bg-primary)',
              borderLeft: '1px solid var(--border-color)', zIndex: 46,
              display: 'flex', flexDirection: 'column',
              boxShadow: '-6px 0 20px rgba(0,0,0,0.16)',
            }}
            role="dialog"
            aria-label="Validations and deviations"
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.625rem 0.875rem', borderBottom: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
            }}>
              <strong style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>Validations & deviations</strong>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeviationsDrawerOpen(false)} title="Close">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem' }}>
              <NoteDeviationPanel items={deviationItems} defaultExpanded={true} />
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Statement"
        message={`Save ${reportName}? The current values will be persisted as the snapshot.`}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={importPriorPyConfirmOpen}
        title="Import PY from Prior CF"
        message="This will populate every Prior-Year cell on the Cash Flow from the prior engagement's saved CF. Any PY values you've already typed will be overwritten. Continue?"
        confirmLabel="Import"
        cancelLabel="Cancel"
        variant="default"
        loading={importPriorPyBusy}
        loadingMessage="Importing…"
        onConfirm={handleImportPriorPy}
        onCancel={() => setImportPriorPyConfirmOpen(false)}
      />
      <ConfirmDialog
        open={excludeConfirmOpen}
        title={`Mark ${reportName} Not Applicable`}
        message={`Skip "${reportName}" from the consolidated FS PDF? Cross-references on other statements will render "—" for this report.`}
        confirmLabel="Mark Not Applicable"
        cancelLabel="Cancel"
        variant="danger"
        loading={isWorking}
        loadingMessage="Excluding…"
        onConfirm={() => performExclude(false)}
        onCancel={() => setExcludeConfirmOpen(false)}
      />
      <ConfirmDialog
        open={clearAndExcludeConfirmOpen}
        title="Clear Saved Data First?"
        message={`"${reportName}" has saved content. Excluding will reset the workspace data and totals. Continue?`}
        confirmLabel="Clear & Exclude"
        cancelLabel="Keep Data"
        variant="danger"
        loading={isWorking}
        loadingMessage="Clearing…"
        onConfirm={() => performExclude(true)}
        onCancel={() => setClearAndExcludeConfirmOpen(false)}
      />
      <ConfirmDialog
        open={transitionConfirm.open}
        title={
          transitionConfirm.kind === 'submit' ? 'Submit Statement' :
          transitionConfirm.kind === 'approve' ? 'Approve Statement' :
          'Reopen to Draft'
        }
        message={
          transitionConfirm.kind === 'submit'
            ? (transitionConfirm.withLiveDrift
                ? `${reportName} has lines that differ from the latest Notes (see the Deviations panel). Submitting keeps the SAVED figures — the ones that will print. Submit anyway? Further edits will be blocked until reopened.`
                : `Submit ${reportName} for review? Further edits will be blocked until reopened.`)
            : transitionConfirm.kind === 'approve'
            ? `Approve ${reportName}? Approved Statements are read-only.`
            : `Reopen ${reportName} to DRAFT? Edits will be allowed again.`
        }
        confirmLabel={
          transitionConfirm.kind === 'submit' ? (transitionConfirm.withLiveDrift ? 'Submit with drift' : 'Submit') :
          transitionConfirm.kind === 'approve' ? 'Approve' :
          'Reopen'
        }
        cancelLabel="Cancel"
        variant={transitionConfirm.kind === 'reopen' || transitionConfirm.withLiveDrift ? 'danger' : 'default'}
        onConfirm={handleConfirmTransition}
        onCancel={() => setTransitionConfirm(prev => ({ ...prev, open: false }))}
      />

      {/* In-place Note Modal — opens above the Statement on "Open
          NOTE_X" action. Closing returns the auditor to the Statement
          unchanged. After save, refetch the Statement so any
          NOTE_TOTAL line whose underlying Note changed picks up the
          new live evaluation. */}
      <FsaNoteModal
        isOpen={openNoteScheduleId != null}
        engagementId={engagementId}
        scheduleId={openNoteScheduleId}
        onClose={() => setOpenNoteScheduleId(null)}
        onSaved={() => { refetch(); }}
        tabId={tabId}
        moduleName={moduleName}
      />

      {/* Statement PDF preview — server-rendered, reads saved workspace
          only. The button above blocks open until at least one Save has
          happened, so this modal can assume the row exists. amountMode
          reflects the screen's display-mode toggle so the PDF matches
          what the auditor was just looking at. */}
      <FsaStatementPdfPreviewModal
        isOpen={previewOpen}
        engagementId={engagementId}
        reportId={reportId}
        reportCode={reportCode}
        reportName={reportName}
        amountMode={displayMode}
        hasUnsavedChanges={isDirty}
        onClose={() => setPreviewOpen(false)}
      />

      {/* FSA-2.4 CF4 — Working Details modal for a CF subsection.
          Mounted when openSubsection is set (via row action on a
          SUBSECTION layout line). Reads evaluated items from
          hydrate.liveEvaluation[code].subsectionItems (carries fresh
          autoValueCy) and saved items from workspace.subsections[code]. */}
      {openSubsection && hydrate && (
        <FsaCfSubsectionWorkingsModal
          isOpen={!!openSubsection}
          subsectionCode={openSubsection.code}
          subsectionLabel={openSubsection.label}
          evaluatedItems={hydrate.liveEvaluation?.[openSubsection.code]?.subsectionItems ?? []}
          savedItems={workspace.subsections?.[openSubsection.code]?.items ?? []}
          displayMode={displayMode}
          roundingFactorKey={hydrate.roundingFactorKey}
          onSave={handleSaveSubsection}
          onClose={() => setOpenSubsection(null)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Layout row — kind-aware rendering
// ============================================================================

const LayoutRow: React.FC<{
  line: StatementLayoutLine;
  workspace: StatementWorkspaceData;
  /**
   * Line values as last PERSISTED. A row whose current value differs from
   * its entry here has been edited and not yet saved, and is marked so the
   * auditor can see at a glance that the screen and the printed statement
   * would disagree. Distinct from drift-vs-Notes (see driftTint): this is
   * "you changed it", that is "the source moved".
   */
  savedLines: StatementWorkspaceData['lines'];
  liveEvaluation: Record<string, EvaluatedLine>;
  /**
   * FSA-2.4 CF — client-side re-evaluated display values per layout
   * line (see liveDisplayValues memo in the parent). Used for SUBTOTAL
   * + TOTAL rendering so formula values reflect modal edits to
   * subsections immediately, before the user clicks Save.
   */
  liveDisplayValues: Map<string, { cy: number; cyR: number; py: number; pyR: number }>;
  displayMode: 'actual' | 'rounded';
  onRefreshLine: (lineCode: string) => void;
  onNavigateToNote: (lineCode: string) => void;
  /**
   * FSA-2.4 CF4 — open the Working Details modal for a SUBSECTION line.
   * Called from the subsection header row's action.
   */
  onOpenSubsection: (code: string, label: string) => void;
  /**
   * FSA-2.4 S3-A — When true (DRAFT status), cells display the LIVE
   * value when it differs from saved (with amber tint + tooltip
   * showing the saved value). When false (SUBMITTED / APPROVED),
   * saved snapshot displays as-is. Mirrors the Notes Modal live-by-
   * default model.
   */
  isLive: boolean;
}> = ({ line, workspace, savedLines, liveEvaluation, liveDisplayValues, displayMode, onRefreshLine, onNavigateToNote, onOpenSubsection, isLive }) => {
  const indent = Math.max(0, line.indent ?? 0);

  const saved = line.code ? workspace.lines[line.code] : undefined;
  const live  = line.code ? liveEvaluation[line.code] : undefined;

  // Row action menu — built unconditionally (Hooks must run before any early
  // return). SECTION_HEADER rows below simply don't render it.
  const actions: ActionMenuItem[] = useMemo(() => {
    const items: ActionMenuItem[] = [];
    if (line.kind === 'NOTE_TOTAL' && line.code) {
      items.push({
        id: 'refresh-line',
        label: 'Refresh from Note',
        icon: <IconSystem.refresh size={14} />,
        onClick: () => onRefreshLine(line.code!),
      });
      // Open Note is unconditional on NOTE_TOTAL rows. The backend now
      // resolves SourceScheduleId from the template even when the source
      // Note has never been saved — so we can always navigate. The Note
      // Modal opens in its "fresh" state when no data exists yet.
      items.push({
        id: 'navigate-note',
        label: `Open ${live?.sourceScheduleCode ?? 'Note'}`,
        icon: <IconSystem.file size={14} />,
        onClick: () => onNavigateToNote(line.code!),
        disabled: !live?.sourceScheduleId,
      });
    } else if ((line.kind === 'SUBTOTAL' || line.kind === 'TOTAL') && line.code) {
      items.push({
        id: 'refresh-line',
        label: 'Refresh from formula',
        icon: <IconSystem.refresh size={14} />,
        onClick: () => onRefreshLine(line.code!),
      });
    }
    return items;
  }, [line.kind, line.code, live?.sourceScheduleId, live?.sourceScheduleCode, onRefreshLine, onNavigateToNote]);

  if (line.kind === 'SECTION_HEADER') {
    return (
      <tr>
        <td colSpan={5} style={{ ...sectionHeaderStyle, paddingLeft: 12 + indent * 16 }}>
          {line.label ?? ''}
        </td>
      </tr>
    );
  }

  // FSA-2.4 CF4 — SUBSECTION renders as a header row (with ActionMenu)
  // + one row per item in the subsection. Item source preference:
  //   1. workspace.subsections[code].items (auditor's saved edits)
  //   2. live.subsectionItems (template auto-seeded defaults)
  // Saved items are enriched with the live evaluation's fresh
  // autoValueCy + autoValueSourceType so source badges update in real
  // time even after the auditor edited via the modal. The header
  // ActionMenu is the single, consistent affordance for opening the
  // Working Details modal — never inline buttons, never a fallback
  // footer row.
  if (line.kind === 'SUBSECTION' && line.code) {
    const liveItems = live?.subsectionItems ?? [];
    const savedItems = workspace.subsections?.[line.code]?.items ?? [];
    const liveById = new Map(liveItems.map((l) => [l.id, l]));
    const items = savedItems.length > 0
      ? [...savedItems]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((s) => {
            const liveMatch = liveById.get(s.id);
            return {
              id: s.id,
              label: s.label,
              order: s.order ?? 0,
              amountActual: s.amount_actual,
              amountRounded: s.amount_rounded,
              priorAmountActual: s.prior_amount_actual,
              priorAmountRounded: s.prior_amount_rounded,
              autoValueCy: liveMatch?.autoValueCy ?? s.auto_value_cy ?? null,
              autoValueCyRounded: liveMatch?.autoValueCyRounded ?? s.auto_value_cy_rounded ?? null,
              autoValueSourceType: liveMatch?.autoValueSourceType ?? null,
              isOverride: s.is_override,
              isSeed: false,
            };
          })
      : liveItems;
    const subTotalCy = items.reduce(
      (sum, i) => sum + (displayMode === 'rounded' ? i.amountRounded : i.amountActual),
      0,
    );
    const headerLabel = line.label ?? line.code;
    const subsectionActions: ActionMenuItem[] = [
      {
        id: 'edit-subsection',
        label: 'Edit items…',
        icon: <IconSystem.edit size={14} />,
        onClick: () => onOpenSubsection(line.code!, line.label ?? line.code!),
      },
    ];
    return (
      <>
        <tr>
          <td colSpan={4} style={{ ...labelTdStyle, paddingLeft: 12 + indent * 16, fontWeight: 500, fontStyle: 'italic', background: '#f8fafc' }}>
            <span>{headerLabel}</span>
            <span
              style={{
                marginLeft: '0.5rem',
                fontSize: '0.7rem',
                color: 'var(--text-secondary)',
                fontWeight: 400,
                fontStyle: 'normal',
              }}
              title={`${items.length} item${items.length === 1 ? '' : 's'} in this subsection · subtotal ₹ ${fmt(subTotalCy)}`}
            >
              · {items.length} item{items.length === 1 ? '' : 's'} · ₹ {fmt(subTotalCy)}
            </span>
          </td>
          <td style={{ ...actionTdStyle, background: '#f8fafc' }}>
            <ActionMenu items={subsectionActions} position="bottom-right" />
          </td>
        </tr>
        {items.map((it) => {
          const cy = displayMode === 'rounded' ? it.amountRounded : it.amountActual;
          const py = displayMode === 'rounded' ? it.priorAmountRounded : it.priorAmountActual;
          const sourceBadge = it.autoValueSourceType
            ? SOURCE_BADGE_LABEL[it.autoValueSourceType] ?? it.autoValueSourceType
            : null;
          // FSA-2.4 CF7 — Workings indicator. Look up the saved item by
          // id to see if it carries narrative + adjustments (workings sheet).
          const savedItem = workspace.subsections?.[line.code!]?.items?.find((s) => s.id === it.id);
          const adjCount = savedItem?.workings?.adjustments?.length ?? 0;
          const hasNarrative = !!savedItem?.workings?.narrative?.trim();
          const hasWorkings = adjCount > 0 || hasNarrative;
          return (
            <tr key={`${line.code}-${it.id}`}>
              <td style={{ ...labelTdStyle, paddingLeft: 12 + (indent + 1) * 16, fontWeight: 400 }}>
                <span>{it.label}</span>
                {sourceBadge && (
                  <span style={subsectionItemSourceBadgeStyle} title={`Auto-derived (${it.autoValueSourceType})`}>
                    {sourceBadge}
                  </span>
                )}
                {hasWorkings && (
                  <span
                    style={workingsIndicatorStyle}
                    title={`Workings: ${adjCount} adjustment${adjCount === 1 ? '' : 's'}${hasNarrative ? ' + narrative' : ''} — open the subsection editor to view.`}
                  >
                    <IconSystem.edit size={9} /> {adjCount > 0 ? `+${adjCount}` : 'note'}
                  </span>
                )}
                {it.isOverride && (
                  <span style={overrideMarkerStyle} title="Auditor override on this item">
                    <IconSystem.edit size={9} /> override
                  </span>
                )}
              </td>
              <td style={noteNumberTdStyle(false)}></td>
              <td style={amountTdStyle}>
                <span style={amountReadOnlyStyle}>{fmt(cy)}</span>
              </td>
              <td style={amountTdStyle}>
                <span style={amountReadOnlyStyle}>{fmt(py)}</span>
              </td>
              <td style={actionTdStyle}></td>
            </tr>
          );
        })}
      </>
    );
  }

  // Drift = saved Statement value differs from the live evaluation
  // (which sums from the latest saved Notes + re-evaluates formulas).
  // For NOTE_TOTAL rows this means the source Note has been edited
  // since the Statement was last refreshed; for SUBTOTAL/TOTAL rows
  // it means one of the upstream lines has moved. Either way, the fix
  // is the same — Refresh.
  // Cross-reference rows (e.g. "Summary of Material Accounting Policies"
  // at the foot of the BS) carry no monetary value — the layout line
  // declared is_reference: true. Skip drift checks and emit blank CY/PY
  // cells below so the row reads as title + Note No. only.
  const isReference = line.kind === 'NOTE_TOTAL' && live?.isReference === true;

  const hasDrift = !isReference &&
    saved != null && live != null && (
      Math.abs((saved.amount_actual ?? 0) - (live.amountActual ?? 0)) > 0.005 ||
      Math.abs((saved.prior_amount_actual ?? 0) - (live.priorAmountActual ?? 0)) > 0.005
    );

  // TB drift = saved Note closing total ≠ live TB sum for the source
  // schedule. Independent of statement-level drift: means the source
  // Note itself is stale (TB classification changed since the Note was
  // last saved). Fix is to Open Note → Save. Only renders on NOTE_TOTAL
  // rows that have a live TB total resolved (null on non-Note rows or
  // engagements without a consolidated TB). Reference rows skip too.
  const hasTbDrift =
    !isReference
    && line.kind === 'NOTE_TOTAL'
    && live != null
    && live.liveTbClosingActual != null
    && Math.abs((live.amountActual ?? 0) - (live.liveTbClosingActual ?? 0)) > 0.005;

  // Source-Note workflow chip — only meaningful on NOTE_TOTAL rows.
  // Shows DRAFT / SUBMITTED / APPROVED so reviewers know which notes
  // are still in flight at a glance.
  const sourceStatus = line.kind === 'NOTE_TOTAL' ? live?.sourceStatusCode ?? null : null;

  // Live note number from the source Note. Resolved at hydrate time
  // (not snapshotted) so renumbering ripples instantly here. Null when
  // the source Note has not been saved yet for this engagement —
  // renderer falls back to no chip.
  const sourceNoteNumber = line.kind === 'NOTE_TOTAL' ? live?.noteNumber ?? null : null;

  // SAVED-AS-DISPLAYED. Cells render the PERSISTED figure — the one the PDF
  // will print — in every status. `driftTint` no longer switches the value
  // shown; it only marks the cell amber to say "the Notes have moved on from
  // this saved figure", with the live number in the tooltip and one click on
  // Save to adopt it.
  //
  // This used to display the LIVE value in DRAFT whenever it differed from
  // saved. That is what let a Balance Sheet look tallied on screen and print
  // untallied: the tallying numbers existed only in the browser. The signal
  // is worth keeping; substituting the value silently was not.
  //
  // Explicit auditor overrides never tint — the difference from live is the
  // whole point of an override, not a discrepancy to flag.
  const driftTint = isLive && hasDrift && !saved?.is_override;

  // Edited-but-not-saved: this row's current value differs from what's
  // persisted. The header carries an "unsaved" chip for the statement as a
  // whole; this points at the actual rows, so the auditor can see precisely
  // which figures would print differently from what's on screen.
  const persisted = line.code ? savedLines[line.code] : undefined;
  const isRowUnsaved = !!line.code
    && !!saved
    && (Math.abs((saved.amount_actual ?? 0) - (persisted?.amount_actual ?? 0)) > 0.005
      || Math.abs((saved.prior_amount_actual ?? 0) - (persisted?.prior_amount_actual ?? 0)) > 0.005
      || (saved.is_override ?? false) !== (persisted?.is_override ?? false));
  // FSA-2.4 CF — SUBTOTAL / TOTAL pull from the client-side re-evaluated
  // map so modal edits to subsections ripple into formula values
  // immediately. VALUE / NOTE_TOTAL keep their existing live-by-default
  // + saved-wins-for-non-overrides semantics.
  const isFormulaRow = line.kind === 'SUBTOTAL' || line.kind === 'TOTAL';
  const computed = line.code ? liveDisplayValues.get(line.code) : undefined;
  // Saved wins. The `live?.*` tail is a first-open fallback only — a line
  // that has never been saved has no persisted figure to show.
  const cyValue = isFormulaRow && computed
    ? (displayMode === 'rounded' ? computed.cyR : computed.cy)
    : (displayMode === 'rounded'
        ? (saved?.amount_rounded ?? live?.amountRounded ?? 0)
        : (saved?.amount_actual ?? live?.amountActual ?? 0));
  const pyValue = isFormulaRow && computed
    ? (displayMode === 'rounded' ? computed.pyR : computed.py)
    : (displayMode === 'rounded'
        ? (saved?.prior_amount_rounded ?? live?.priorAmountRounded ?? 0)
        : (saved?.prior_amount_actual ?? live?.priorAmountActual ?? 0));
  const isBold = line.is_bold ?? (line.kind === 'SUBTOTAL' || line.kind === 'TOTAL');

  return (
    <tr
      style={
        // Unsaved edits get a left rule + tinted row so they're findable by
        // scanning, not just by reading every number.
        isRowUnsaved
          ? { ...(isBold ? boldRowStyle : rowStyle), ...unsavedRowStyle }
          : (isBold ? boldRowStyle : rowStyle)
      }
    >
      <td style={{ ...labelTdStyle, paddingLeft: 12 + indent * 16, fontWeight: isBold ? 700 : 400 }}>
        <span>{line.label ?? line.code}</span>
        {isRowUnsaved && (
          <span
            style={unsavedRowChipStyle}
            title="Edited since the last save. The printed statement still shows the saved figure until you Save."
          >
            unsaved
          </span>
        )}
        {sourceStatus && (
          <span style={sourceStatusChipStyleFor(sourceStatus)} title={`Source Note status: ${sourceStatus}`}>
            {sourceStatus}
          </span>
        )}
        {/* FSA-2.4 S1-C — drift chips demoted to muted dots. Aggregate
            counts surface in the top Deviations panel; the per-line
            dot is the drill-in indicator with the amount in tooltip.
            Two distinct dots: amber for statement-line drift (saved ≠
            live), red for TB drift (source Note stale vs live TB). */}
        {hasDrift && (
          <span
            style={statementDriftDotStyle('warning')}
            title={`Statement-line drift ₹ ${fmt(Math.abs((saved?.amount_actual ?? 0) - (live?.amountActual ?? 0)))} — saved value differs from live evaluation. Click Save to refresh and persist.`}
            aria-label={`Statement-line drift ${fmt(Math.abs((saved?.amount_actual ?? 0) - (live?.amountActual ?? 0)))}`}
          >
            ●
          </span>
        )}
        {hasTbDrift && (
          <span
            style={statementDriftDotStyle('error')}
            title={`TB drift ₹ ${fmt(Math.abs((live?.amountActual ?? 0) - (live?.liveTbClosingActual ?? 0)))} — source Note's saved closing differs from live TB. Open the Note and Re-save to refresh.`}
            aria-label={`TB drift ${fmt(Math.abs((live?.amountActual ?? 0) - (live?.liveTbClosingActual ?? 0)))}`}
          >
            ●
          </span>
        )}
        {/* FSA-2.4 S3-D — Override marker. `is_override: true` lines are
            preserved through Refresh All AND Save auto-apply (per locked
            spec: override = explicit auditor intent). The auditor sees
            a small pencil pill so they know this line won't auto-track
            the Note. Aggregate count surfaces in the Deviations panel
            as an info row. */}
        {saved?.is_override && (
          <span
            style={overrideMarkerStyle}
            title="Auditor override — this value is preserved through Refresh and Save. Clear the override by clicking Refresh from Note on the row's action menu."
          >
            <IconSystem.edit size={9} /> override
          </span>
        )}
      </td>
      <td style={noteNumberTdStyle(isBold)}>
        {sourceNoteNumber != null
          ? (live?.sourceScheduleId && line.code
              ? (
                <button
                  type="button"
                  style={noteNumberLinkStyle}
                  title={`Open source Note ${sourceNoteNumber}`}
                  onClick={() => onNavigateToNote(line.code!)}
                >
                  {sourceNoteNumber}
                </button>
              )
              : sourceNoteNumber)
          : ''}
      </td>
      <td style={amountTdStyle}>
        {!isReference && (
          <span
            style={driftTint ? liveOverrideAmountStyle : amountReadOnlyStyle}
            title={driftTint
              ? `Saved: ${fmt(cyValue)} · Latest from Notes: ${fmt(displayMode === 'rounded' ? (live?.amountRounded ?? 0) : (live?.amountActual ?? 0))} — Save to adopt.`
              : undefined}
          >
            {fmt(cyValue)}
          </span>
        )}
      </td>
      <td style={amountTdStyle}>
        {!isReference && (
          <span
            style={driftTint ? liveOverrideAmountStyle : amountReadOnlyStyle}
            title={driftTint
              ? `Saved: ${fmt(pyValue)} · Latest from Notes: ${fmt(displayMode === 'rounded' ? (live?.priorAmountRounded ?? 0) : (live?.priorAmountActual ?? 0))} — Save to adopt.`
              : undefined}
          >
            {fmt(pyValue)}
          </span>
        )}
      </td>
      <td style={actionTdStyle}>
        {actions.length > 0 && (
          <ActionMenu items={actions} position="bottom-right" />
        )}
      </td>
    </tr>
  );
};

// ============================================================================
// Helpers
// ============================================================================

function fmt(n: number): string {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateTime(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ============================================================================
// Styles
// ============================================================================

const screenStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  position: 'relative', // positioning context for the deviations drawer
};
// FSA-2.4.4 — Focus mode overlay: lift the screen above the app sidebar / bar
// to a max-area review surface. CSS overlay (not the Fullscreen API) so
// portaled modals/toasts still stack above it.
const focusOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  // Above the app bar + engagement workspace header so focus mode is truly
  // full-bleed (otherwise those sticky/fixed bars float over the statement's
  // sticky column header). Still below portaled modals/toasts (z-index 9998+).
  zIndex: 1300,
  background: 'var(--bg-primary)',
};
const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.625rem 0.875rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  flexWrap: 'wrap',
  gap: 8,
};
const headerTitleStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem', color: 'var(--text-primary)',
};
const headerSubtitleStyle: React.CSSProperties = {
  fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2,
};
const headerActionsStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
};
const dirtyChipStyle: React.CSSProperties = {
  padding: '1px 6px', borderRadius: 999, background: '#fef3c7', color: '#92400e',
  fontSize: '0.7rem', fontWeight: 600,
};
const excludedChipStyle: React.CSSProperties = {
  padding: '1px 8px', borderRadius: 999, background: '#f1f5f9', color: '#475569',
  fontSize: '0.7rem', fontWeight: 600, fontStyle: 'italic',
};
const excludedPlaceholderStyle: React.CSSProperties = {
  flex: 1, minHeight: 0,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: '0.75rem', padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)',
};
const excludedPlaceholderTitleStyle: React.CSSProperties = {
  fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)',
};
const excludedPlaceholderSubtitleStyle: React.CSSProperties = {
  fontSize: '0.85rem', maxWidth: 480, lineHeight: 1.5,
};
// (drift chip styles moved to RowBadges.tsx — see DriftChip / LedgerDriftChip.)
// "Note N" cross-reference chip on NOTE_TOTAL rows. Note number is
// resolved LIVE (not snapshotted) so a renumber on any Note ripples
// straight to every Statement row that references it on next hydrate.
// Note No. column — narrow center-aligned cell rendered between
// Particulars and CY (per audited-FS convention). When the source Note
// is resolvable, the number is rendered as a button styled like a link
// so the auditor can drill into the Note Modal with a single click.
function noteNumberTdStyle(isBold: boolean): React.CSSProperties {
  return {
    padding: '0.5rem 0.5rem',
    color: 'var(--text-primary, #1e293b)',
    borderBottom: '1px solid var(--border-color, #e2e8f0)',
    textAlign: 'center',
    fontSize: '0.875rem',
    fontWeight: isBold ? 700 : 400,
    whiteSpace: 'nowrap',
    width: 80,
  };
}
const thNoteStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '0.75rem 0.5rem',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--text-primary, #1e293b)',
  background: 'var(--grid-header-bg, #dbeafe)',
  borderBottom: '2px solid var(--border-color, #e2e8f0)',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  width: 80,
};
const noteNumberLinkStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  padding: 0,
  color: '#2563eb',
  cursor: 'pointer',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};
function statusChipStyleFor(status: string): React.CSSProperties {
  const colour =
    status === 'APPROVED' ? { bg: '#dcfce7', fg: '#166534' } :
    status === 'SUBMITTED' ? { bg: '#dbeafe', fg: '#1e40af' } :
    { bg: '#f1f5f9', fg: '#475569' };
  return {
    padding: '1px 8px', borderRadius: 999,
    background: colour.bg, color: colour.fg,
    fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.4,
  };
}
// Smaller variant for inline source-Note status chips next to NOTE_TOTAL
// row labels. Reuses statusChipStyleFor's colour palette but with the
// in-cell margin + font sizing that fits inside the label cell.
function sourceStatusChipStyleFor(status: string): React.CSSProperties {
  return {
    ...statusChipStyleFor(status),
    marginLeft: 8,
    padding: '1px 6px',
    fontSize: '0.62rem',
    fontWeight: 700,
    textTransform: 'uppercase',
  };
}
// FSA-2.4 S1-D — Actual/Rounded display toggle restyled to pill (rounded
// corners, full radius) matching the Note Modal's header toggle. Same
// pattern across all FSA mode toggles for visual consistency.
const displayToggleWrapStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'stretch',
  border: '1px solid var(--border-color)', borderRadius: 999,
  overflow: 'hidden',
  background: 'var(--bg-secondary)',
};
function displayToggleBtnStyle(active: boolean): React.CSSProperties {
  return {
    padding: '2px 10px',
    fontSize: '0.7rem',
    fontWeight: 600,
    background: active ? '#2563eb' : 'transparent',
    color: active ? '#ffffff' : 'var(--text-secondary)',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}
// FSA-2.4 S1-C — Per-line drift dot (replaces DriftChip / LedgerDriftChip
// on Statement rows). Amber for statement-line drift; red for TB drift
// (which represents a stale source Note + has a different remediation).
// Aggregates surface in the top Deviations panel; the row dot is the
// drill-in cue with the amount in tooltip.
const statementDriftDotStyle = (severity: 'warning' | 'error'): React.CSSProperties => ({
  marginLeft: 8,
  color: severity === 'error' ? '#991b1b' : '#d97706',
  fontSize: '0.9rem',
  cursor: 'help',
  lineHeight: 1,
});
// FSA-2.4 S3-D — Override marker pill. Sits next to source-status /
// drift dots. Indigo to distinguish from drift colours; small icon +
// "override" label.
const overrideMarkerStyle: React.CSSProperties = {
  marginLeft: 6,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  padding: '1px 6px',
  borderRadius: 999,
  background: '#e0e7ff',
  color: '#4338ca',
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  cursor: 'help',
};

// FSA-2.4 CF4 — subsection item source-badge pill ("from Note" /
// "from BS Δ" etc.). Visually quieter than the override marker so the
// auditor can scan items and spot which are auto-derived vs manual.
const subsectionItemSourceBadgeStyle: React.CSSProperties = {
  marginLeft: 6,
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0px 5px',
  borderRadius: 999,
  background: '#dbeafe',
  color: '#1e40af',
  fontSize: '0.6rem',
  fontWeight: 600,
  letterSpacing: 0.2,
  cursor: 'help',
};

// FSA-2.4 CF7 — workings indicator on a subsection item. Shows when the
// auditor has manual adjustments / narrative attached to the item. Amber-
// tinted so it stands apart from the blue source badge.
const workingsIndicatorStyle: React.CSSProperties = {
  marginLeft: 6,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  padding: '0px 5px',
  borderRadius: 999,
  background: '#fef3c7',
  color: '#92400e',
  fontSize: '0.6rem',
  fontWeight: 600,
  letterSpacing: 0.2,
  cursor: 'help',
};

const SOURCE_BADGE_LABEL: Record<string, string> = {
  bs_movement:    'from BS Δ',
  note_total:     'from Note',
  note_class:     'from Note class',
  column_sum:     'from column',
  statement_line: 'from PL',
  sum_of:         'composed',
};

// FSA-2.4.2 — Badges row sits below the header (title + actions). Carries
// status / dirty / live-drift chips on the left, display toggle on the
// right. Tight padding keeps the row ~30px tall.

// FSA-2.4.1 — Statement summary strip styles (compact single-row-per-item).
// Replaced the original 3-column stacked grid with a vertical list of
// inline rows (label + CY + PY on one line per item). Recovers ~80px
// of table room on small viewports. Collapsible via the header chevron.
const summaryStripStyle: React.CSSProperties = {
  margin: '0 1rem 6px',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  background: 'var(--bg-secondary)',
  overflow: 'hidden',
  flexShrink: 0,
};
const summaryStripHeaderButtonStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '3px 12px',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  background: 'var(--bg-primary)',
  border: 'none',
  borderBottom: '1px solid var(--border-color)',
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const summaryStripChevronStyle = (expanded: boolean): React.CSSProperties => ({
  fontSize: '0.8rem',
  transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
  transition: 'transform 120ms',
  color: 'var(--text-secondary)',
});
// FSA-2.4.3 — Transposed summary layout.
// Two rows total (CY, PY), no column-header row. Each row has a small
// "CY"/"PY" anchor on the left and three inline labelled values on the
// right (TA · TE+L · Drift for BS; Income · Expenses · Profit for PL).
// More compact than the 2x3 grid AND clearer for a side-by-side period
// comparison since both periods share the same horizontal structure.
const summaryStripBodyStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};
const summaryStripPeriodRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '4px 12px',
  borderTop: '1px solid var(--border-color)',
};
const summaryStripPeriodAnchorStyle: React.CSSProperties = {
  fontWeight: 700,
  color: 'var(--text-secondary)',
  fontSize: '0.72rem',
  letterSpacing: 0.5,
  minWidth: 28,
};
const summaryStripPeriodValuesStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 18,
  flexWrap: 'wrap',
};
const summaryStripInlineValueStyle = (
  isDrift?: boolean,
  emphasis?: 'positive' | 'negative' | 'neutral',
): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: 6,
  padding: isDrift || emphasis ? '1px 8px' : 0,
  borderRadius: isDrift || emphasis ? 4 : 0,
  background: isDrift
    ? 'transparent'
    : emphasis === 'positive'
      ? 'rgba(22,163,74,0.08)'
      : emphasis === 'negative'
        ? 'rgba(217,119,6,0.08)'
        : 'transparent',
});
const summaryStripShortLabelStyle: React.CSSProperties = {
  fontSize: '0.66rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
};
const summaryStripAmountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
  fontSize: '0.82rem',
};
// FSA-2.4.2 — Drift status pill in the strip header. Visible even when
// the strip body is collapsed so the auditor never misses an
// out-of-balance state. Two-state: green ✓ Reconciled / amber ⚠ Out of
// Balance ₹X.
const summaryStripHeaderLeftStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
const summaryStripHeaderDriftStyle = (reconciled: boolean): React.CSSProperties => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  padding: '1px 8px',
  borderRadius: 999,
  background: reconciled ? '#dcfce7' : '#fef3c7',
  color: reconciled ? '#166534' : '#92400e',
});

// Wrapper for the scroll area — mirrors `.syut-grid-wrapper` semantics:
// flex: 1 + overflow: auto, no padding so the sticky thead anchors at
// the top of the viewport.
const tableScrollStyle: React.CSSProperties = {
  flex: 1, minHeight: 0, overflow: 'auto',
  background: 'var(--bg-primary, #ffffff)',
};
// `.syut-grid` look: full-width, border-collapse, 0.875rem font.
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
  background: 'var(--bg-primary, #ffffff)',
};
// Sticky on thead. SyutGrid's CSS uses sticky on `th`; either works,
// but `thead` keeps a single stacking context for the whole header row.
const theadStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 3,
};
// Header cells — exact SyutGrid `th` styling (#dbeafe, 0.75rem 1rem
// padding, 2px solid border-bottom, 600 weight, no wrap).
const thLabelStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.75rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--text-primary, #1e293b)',
  background: 'var(--grid-header-bg, #dbeafe)',
  borderBottom: '2px solid var(--border-color, #e2e8f0)',
  whiteSpace: 'nowrap',
  userSelect: 'none',
};
const thAmountStyle: React.CSSProperties = {
  ...thLabelStyle,
  textAlign: 'right',
};
// SECTION_HEADER rows render as a band — distinct enough from regular
// rows to read as a section break without competing with the sticky
// header above.
const sectionHeaderStyle: React.CSSProperties = {
  background: 'var(--bg-secondary, #f8fafc)',
  padding: '0.625rem 1rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  fontSize: '0.75rem',
  color: 'var(--text-secondary, #64748b)',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};
// Regular + bold rows. Match SyutGrid `td` padding (0.625rem 1rem) +
// 1px solid border-bottom. Bold rows (SUBTOTAL / TOTAL) get a softer
// background tint so they read as totals at a glance.
const rowStyle: React.CSSProperties = { background: 'var(--bg-primary, #ffffff)' };
const boldRowStyle: React.CSSProperties = { background: 'var(--bg-secondary, #f8fafc)' };
const labelTdStyle: React.CSSProperties = {
  padding: '0.625rem 1rem',
  color: 'var(--text-primary, #1e293b)',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};
const amountTdStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  color: 'var(--text-primary, #1e293b)',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  textAlign: 'right',
};
const actionTdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  textAlign: 'center',
  width: 60,
};
const amountReadOnlyStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
};
// FSA-2.4 S3-A — Live-by-default cell tint. When DRAFT and the line
// has drift between saved and live, the cell DISPLAYS the live value
// with this amber pill so the auditor sees the latest reality. Save
// auto-applies the live values (handler logic in S3-B) so a single
// click persists what the auditor sees.
const liveOverrideAmountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  background: '#fef3c7',
  borderRadius: 3,
  padding: '1px 6px',
  display: 'inline-block',
};
// Row-level "edited, not saved" marker. Deliberately a different signal from
// the amber amount tint (which means "the Notes moved on from this saved
// figure") — one is the auditor's own pending change, the other is the
// source's. Blue reads as informational; amber stays reserved for drift.
const unsavedRowStyle: React.CSSProperties = {
  background: '#eff6ff',
  boxShadow: 'inset 3px 0 0 0 #2563eb',
};
const unsavedRowChipStyle: React.CSSProperties = {
  marginLeft: 6,
  padding: '0 6px',
  borderRadius: 999,
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  background: '#dbeafe',
  color: '#1e40af',
  verticalAlign: 'middle',
};
const loaderStyle: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: 'var(--text-secondary)', fontSize: '0.9rem',
};
const errorStyle: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#b91c1c', fontSize: '0.9rem',
};

export default FsaStatementScreen;
